import React from 'react';
import type {
  ContentSyncSummary,
  FactoryCourseImpact,
} from 'services/api/adminContentClient';
import { cn } from 'lib/utils';

type Props = {
  sync?: ContentSyncSummary | null;
  impacts?: FactoryCourseImpact[];
  subjectLabel?: string;
};

const stateCopy: Record<ContentSyncSummary['state'], { title: string; body: string }> = {
  in_sync: {
    title: 'Course Factory: in sync',
    body: 'The site matches the last reconciled Course Source revision.',
  },
  site_changes_pending: {
    title: 'Course Factory: site changes pending',
    body: 'You can save here. Course Factory will preserve and reconcile these site changes before its next build.',
  },
  agent_draft_pending: {
    title: 'Course Factory: agent draft pending',
    body: 'A newer Course Source draft exists. Saving here remains allowed and will be checked before the next build.',
  },
  reconciliation_required: {
    title: 'Course Factory: reconciliation required',
    body: 'Both the site and Course Source changed. The next build is paused until overlapping edits are resolved.',
  },
};

const styleByState: Record<ContentSyncSummary['state'], string> = {
  in_sync: 'border-emerald-300/60 bg-emerald-50 text-emerald-950',
  site_changes_pending: 'border-amber-300/70 bg-amber-50 text-amber-950',
  agent_draft_pending: 'border-blue-300/70 bg-blue-50 text-blue-950',
  reconciliation_required: 'border-orange-400/70 bg-orange-50 text-orange-950',
};

const ContentSyncNotice: React.FC<Props> = ({ sync, impacts = [], subjectLabel = 'content' }) => {
  const records = sync ? [{ course_id: 0, course_name: '', ...sync }] : impacts;
  if (records.length === 0) return null;

  const primary = records.some((record) => record.state === 'reconciliation_required')
    ? records.find((record) => record.state === 'reconciliation_required')!
    : records.some((record) => record.state === 'site_changes_pending')
      ? records.find((record) => record.state === 'site_changes_pending')!
      : records.some((record) => record.state === 'agent_draft_pending')
        ? records.find((record) => record.state === 'agent_draft_pending')!
        : records[0];
  const copy = stateCopy[primary.state];

  return (
    <div
      className={cn('rounded-xl border p-3 text-sm', styleByState[primary.state])}
      role="status"
      data-testid="course-factory-sync-notice"
    >
      <p className="font-semibold">{copy.title}</p>
      <p className="mt-1 text-xs leading-relaxed">{copy.body}</p>
      {records.length > 1 ? (
        <div className="mt-2 text-xs">
          <p className="font-medium">
            This shared {subjectLabel} affects {records.length} factory-managed courses:
          </p>
          <ul className="mt-1 list-disc pl-5">
            {records.map((record) => (
              <li key={record.course_id}>{record.course_name}</li>
            ))}
          </ul>
        </div>
      ) : impacts.length === 1 ? (
        <p className="mt-2 text-xs">Affected course: {impacts[0].course_name}</p>
      ) : null}
    </div>
  );
};

export default ContentSyncNotice;
